import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { recordRecent } from '../hooks/use-recents';

export type PageEntity = { type: string; id: string; name: string; data?: Record<string, unknown> } | null;

const Ctx = createContext<{ entity: PageEntity; set: (e: PageEntity) => void }>({ entity: null, set: () => {} });

export function PageContextProvider({ children }: { children: ReactNode }) {
  const [entity, set] = useState<PageEntity>(null);
  return <Ctx.Provider value={{ entity, set }}>{children}</Ctx.Provider>;
}

export function usePageEntity(): PageEntity {
  return useContext(Ctx).entity;
}

export function useRegisterPageEntity(entity: PageEntity): void {
  const { set } = useContext(Ctx);
  const key = entity ? `${entity.type}:${entity.id}` : 'none';
  useEffect(() => {
    set(entity);
    // Track visited records for the sidebar's Recent list (no-op for views).
    if (entity) recordRecent({ type: entity.type, id: entity.id, name: entity.name });
    return () => set(null);
    // Re-register only when the identity or name changes, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, entity?.name]);
}

// Convenience wrapper for list/index screens that are a view rather than a single
// record. Registers a lightweight `view` entity so the assistant always knows which
// page is open, without a page needing to hand-build the entity shape.
export function useRegisterPageView(id: string, name: string): void {
  useRegisterPageEntity({ type: 'view', id, name });
}
